import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { OrganizationKeyName } from "../../model/Organization";
import { MonitorEventData } from "../../service/api-response/MonitorEventsSuccessResponse";
import { RootState } from "../../store";
import * as userMonitorEventSelectors from "./userMonitorEventSelectors";
import * as userMonitorEventActions from "./userMonitorEventActions";
import * as authSelectors from "../auth/authSelectors";
import Spinner from "../common/Spinner";
import { MonitorEventListFilters } from "../../service/ApiMonitorEvent";
import { MonitorEventListFiltersForm } from "./MonitorEventListFiltersForm";
import * as _ from "lodash";

interface OwnProps {
  organization: OrganizationKeyName;
  userId?: string;
  email?: string;
  name?: string;
  fromDate?: string;
  toDate?: string;
  eventName?: string;
  isVisibleEventTypeFilter?: boolean;
  isVisibleEmailFilter?: boolean;
}

interface Props {
  isLoading: (filters: MonitorEventListFilters) => boolean;
  hasMore: (filters: MonitorEventListFilters) => boolean;
  items: (filters: MonitorEventListFilters) => MonitorEventData[] | undefined;
  getItems: (
    org: OrganizationKeyName,
    filters: MonitorEventListFilters
  ) => void;
  getMoreItems: (
    org: OrganizationKeyName,
    filters: MonitorEventListFilters
  ) => void;
  organization: OrganizationKeyName;
}

interface State {
  filters: {
    userId?: string;
    email?: string;
    name?: string;
    fromDate?: string;
    toDate?: string;
    eventName?: string;
  };
}

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
  const org = authSelectors.selectedOrganizationKeyName(state);

  return {
    isLoading: (filters: MonitorEventListFilters) =>
      userMonitorEventSelectors.isLoading(state, org, filters),
    hasMore: (filters: MonitorEventListFilters) =>
      userMonitorEventSelectors.hasMoreForUser(state, org, filters),
    items: (filters: MonitorEventListFilters) =>
      userMonitorEventSelectors.listForUser(state, org, filters),
    organization: org,
  };
};

const mapDispatchToProps = (dispatch: Dispatch, ownProps: OwnProps) => {
  return {
    getItems: (
      orgKeyName: OrganizationKeyName,
      filters: MonitorEventListFilters
    ) =>
      dispatch(
        userMonitorEventActions.getUserMonitorEventListAction(
          orgKeyName,
          userMonitorEventActions.GetUserMonitorSubtype.LoadFirstPage,
          filters
        )
      ),
    getMoreItems: (
      orgKeyName: OrganizationKeyName,
      filters: MonitorEventListFilters
    ) =>
      dispatch(
        userMonitorEventActions.getUserMonitorEventListAction(
          orgKeyName,
          userMonitorEventActions.GetUserMonitorSubtype.LoadNextPage,
          filters
        )
      ),
  };
};

class MonitorEventList extends React.Component<Props & OwnProps, State> {
  state: State = {
    filters: {},
  };

  isEmailFilterVisible() {
    return !!this.props.isVisibleEmailFilter;
  }

  isEventNameFilterVisible() {
    return !!this.props.isVisibleEventTypeFilter;
  }

  onFiltersReset = () => {
    this.setState({ filters: {} }, () => {
      this.props.getItems(this.props.organization, this.getFilters());
    });
  };

  onFiltersSearch = (values: any) => {
    this.setState({ filters: { ...values } }, () => {
      this.props.getItems(this.props.organization, this.getFilters());
    });
  };

  isFromDateFilterVisible(): boolean {
    return _.isUndefined(this.props.fromDate);
  }

  isToDateFilterVisible(): boolean {
    return _.isUndefined(this.props.toDate);
  }

  getFilters(): MonitorEventListFilters {
    return {
      name: this.props.name || this.state.filters.name,
      email: this.props.email || this.state.filters.email,
      userId: this.props.userId || this.state.filters.userId,
      fromDate: this.props.fromDate || this.state.filters.fromDate,
      toDate: this.props.toDate || this.state.filters.toDate,
      eventName: this.props.eventName || this.state.filters.eventName,
    };
  }

  componentDidMount() {
    this.props.getItems(this.props.organization, this.getFilters());
  }

  onLoadMoreBtnClick = () => {
    this.props.getMoreItems(this.props.organization, this.getFilters());
  };

  hasMore = () => this.props.hasMore(this.getFilters());

  isLoading = () => this.props.isLoading(this.getFilters());

  items = () => this.props.items(this.getFilters()) || [];

  render() {
    return (
      <>
        <MonitorEventListFiltersForm
          isFromDateVisible={this.isFromDateFilterVisible()}
          isToDateVisible={this.isToDateFilterVisible()}
          isEmailVisible={this.isEmailFilterVisible()}
          isEventNameVisible={this.isEventNameFilterVisible()}
          onReset={this.onFiltersReset}
          onSearch={this.onFiltersSearch}
        />
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">User Monitor Events</h3>
          </div>
          <div className="table-responsive">
            <table className="table table-hover table-outline table-vcenter card-table">
              <thead>
                <tr>
                  <th>Id</th>
                  <th className="text-center">Date</th>
                  {this.isEmailFilterVisible() && (
                    <th className="text-center">Email</th>
                  )}
                  <th className="text-center">Event</th>
                  <th>Params</th>
                </tr>
              </thead>
              <tbody>
                {this.items().map((x) => (
                  <tr key={x.id}>
                    <td>{x.id}</td>
                    <td>{x.date}</td>
                    {this.isEmailFilterVisible() && <td>{x.userEmail}</td>}
                    <td>{x.eventName}</td>
                    <td>{JSON.stringify(x.params)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="card-footer text-center">
            {this.isLoading() ? (
              <div className="text-center">
                <Spinner />
              </div>
            ) : (
              <>
                {this.hasMore() ? (
                  <button
                    type="button"
                    onClick={() => this.onLoadMoreBtnClick()}
                  >
                    Load more
                  </button>
                ) : (
                  <span>No more event logs</span>
                )}
              </>
            )}
          </div>
        </div>
      </>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(MonitorEventList);
